import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { defaultSetId, getSet } from '@/content/registry';
import { selectStats, selectVisibleGroups } from '@/domain/selectors';
import { useT } from '@/i18n/useT';
import { useProgress } from '@/state/progressStore';
import { useSettings, viewFor } from '@/state/settingsStore';
import { useToday } from '@/state/useToday';
import { adapter, syncEngine } from '@/sync/engine';
import { useSyncLifecycle } from '@/sync/useSyncLifecycle';
import { useToast } from '@/state/toastStore';
import { TopBar } from '@/components/layout/TopBar';
import { StatsBar } from '@/components/layout/StatsBar';
import { NowCard } from '@/components/layout/NowCard';
import { FilterBar } from '@/components/filters/FilterBar';
import { GroupSection } from '@/components/tracker/GroupSection';
import { SettingsSheet } from '@/components/settings/SettingsSheet';
import { ShareSheet } from '@/components/share/ShareSheet';
import { Toast } from '@/components/ui/Toast';

// The map, its geometry and its share renderer only load when someone opens
// the map view — list users never pay for them.
const MapView = lazy(() => import('@/map/MapView'));

export function App() {
  const { t, lang } = useT();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const ready = useProgress((s) => s.ready);
  const attach = useProgress((s) => s.attach);
  const snapshot = useProgress((s) => s.snapshot);
  const activeProfileId = useProgress((s) => s.activeProfileId);

  const storedProfileId = useSettings((s) => s.activeProfileId);
  const setActiveProfileId = useSettings((s) => s.setActiveProfileId);
  const activeSetId = useSettings((s) => s.activeSetId);
  const filters = useSettings((s) => s.filters);
  const scanlines = useSettings((s) => s.scanlines);
  const views = useSettings((s) => s.views);
  const today = useToday();
  const showToast = useToast((s) => s.show);

  useEffect(() => {
    void attach(adapter, storedProfileId);
    // Intentionally once on mount: the adapter identity never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pairs from a scanned link, pulls on focus, flushes when the tab hides.
  // Arriving by QR is the one pairing path with no button to give feedback,
  // so it says so itself.
  useSyncLifecycle(syncEngine, (ok) =>
    showToast({ message: t(ok ? 'sync.paired' : 'sync.pairFailed') }),
  );

  // Remember whichever profile the adapter settled on.
  useEffect(() => {
    if (activeProfileId && activeProfileId !== storedProfileId) {
      setActiveProfileId(activeProfileId);
    }
  }, [activeProfileId, storedProfileId, setActiveProfileId]);

  const set = getSet(activeSetId ?? '') ?? getSet(defaultSetId());

  useEffect(() => {
    if (set) document.title = `${set.title[lang]} — ${t('app.name')}`;
  }, [set, lang, t]);

  const ctx = useMemo(
    () => ({
      isDone: (itemId: string): boolean =>
        set ? snapshot.sets[set.id]?.[itemId]?.done === true : false,
    }),
    [snapshot, set],
  );

  const stats = useMemo(() => (set ? selectStats(set, ctx) : null), [set, ctx]);
  const visibleGroups = useMemo(
    () => (set ? selectVisibleGroups(set, filters, ctx) : []),
    [set, filters, ctx],
  );

  const mapMode = set?.map !== undefined && viewFor(views, set.id, true) === 'map';

  if (!set || !stats) {
    return (
      <main className="p-4 text-text-dim">
        No content sets found. Run <code className="text-pip">npm run content:build</code>.
      </main>
    );
  }

  return (
    <div className={`min-h-full ${scanlines ? 'scanlines' : ''}`}>
      <div className="sticky top-0 z-20">
        <TopBar
          set={set}
          onOpenShare={() => setShareOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
        />
        <StatsBar stats={stats} />
        <FilterBar
          counts={{
            all: stats.total,
            done: stats.done,
            todo: stats.total - stats.done,
          }}
        />
      </div>

      <main className="mx-auto max-w-5xl px-2 py-3">
        {ready && !mapMode ? <NowCard set={set} stats={stats} today={today} /> : null}
        {!ready ? (
          <p className="px-1 py-6 text-center text-[12px] leading-4 text-text-faint">…</p>
        ) : mapMode ? (
          <Suspense
            fallback={
              <p className="px-1 py-6 text-center text-[12px] leading-4 text-text-faint">
                {t('map.loading')}
              </p>
            }
          >
            <MapView set={set} filters={filters} ctx={ctx} stats={stats} />
          </Suspense>
        ) : visibleGroups.length === 0 ? (
          <div className="px-1 py-10 text-center">
            <p className="text-[13px] leading-5 text-text-dim">{t('filter.empty')}</p>
            <p className="mt-1 text-[11px] leading-4 text-text-faint">{t('filter.emptyHint')}</p>
          </div>
        ) : (
          visibleGroups.map((visible) => (
            <GroupSection
              key={visible.group.id}
              setId={set.id}
              visible={visible}
              today={today}
            />
          ))
        )}

        {/*
          Sources live here rather than in Settings: they are attribution, not
          a preference, and the people the links are for — anyone checking a
          drop chance — are not going to open a settings drawer to find them.
        */}
        <footer className="flex flex-wrap items-center gap-x-1.5 gap-y-1 px-1 pt-2 pb-6 text-[10px] leading-4 text-text-faint">
          <span>
            {t('app.name')} · {t('app.tagline')} · {set.contentVersion}
          </span>
          <span aria-hidden="true">·</span>
          <span>{t('footer.sources')}:</span>
          {[...(set.sourceUrls ?? []), 'https://rueso.ru/'].map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              {...(url.includes('rueso.ru') ? { title: t('footer.ruesoTitle') } : {})}
              className="text-pip-dim underline hover:text-pip"
            >
              {new URL(url).hostname}
            </a>
          ))}
        </footer>
      </main>

      {settingsOpen ? <SettingsSheet set={set} onClose={() => setSettingsOpen(false)} /> : null}
      {shareOpen ? <ShareSheet set={set} onClose={() => setShareOpen(false)} /> : null}
      <Toast />
    </div>
  );
}
